<script>
// layout
import Authenticated from "@/Layouts/Authenticated";
// components
import SupplierTabs from './Components/SupplierTabs.vue';
// vue packages
import {Link, Head, usePage, useForm} from "@inertiajs/vue3";

export default {
    layout: Authenticated,
    components: {
        SupplierTabs,
        Head,
    },
    setup(props) {
        const organisation = usePage().props.organisation ?? null;
        const commissionRates = usePage().props.commissionRates ?? null;
        const updating = usePage().props.updating;

        return {
            organisation,
            commissionRates,
            updating,
        }
    },
    data() {
        return {
            form: useForm({
                commission_rate: "",
                start_date: "",
                end_date: "",
            })
        }
    },
    methods: {
        submit() {
            this.form.post(route('frontend.organisations.suppliers.organisation.commission-rate.store', this.organisation.id))
        }
    }
}
</script>
<template>
    <Head v-if="updating === false" title="Create New Organisation"/>
    <Head v-else :title="organisation.name"/>
    <ValidationErrors :show="showErrors"/>
    <div class="w-full space-y-3 mb-6">
        <div>
            <h1 class="mt-2 text-2xl font-bold leading-7 text-gray-900 sm:text-3xl sm:truncate">
                <span
                    v-if="organisation.name === null"
                    v-html="'Create New ' + organisation.type.charAt(0).toUpperCase() + organisation.type.substring(1)">
                </span>
                <span v-else v-html="organisation.name"></span>
            </h1>
            <h2 v-if="organisation.registered_legal_name === null"
                class="mb-2 mt-0 text-md font-medium leading-7 text-gray-500 sm:truncate"
                v-html="''"></h2>
            <h2 v-else class="mb-2 mt-0 text-md font-medium leading-7 text-gray-500 sm:truncate"
                v-html="organisation.registered_legal_name"></h2>
        </div>
        <div class="space-y-6 mt-2 lg:col-span-9">
            <section aria-labelledby="payment-details-heading">
                <SupplierTabs v-if="organisation" :organisation="organisation"/>
                <div class="shadow sm:rounded-md sm:overflow-hidden">
                    <div class="bg-white px-4 p-6 space-y-6">
                        <div id="supplier_form" class="space-y-6">
                            <div class="relative">
                                <div class="absolute inset-0 flex items-center" aria-hidden="true">
                                    <div class="w-full border-t border-gray-300"/>
                                </div>
                                <div class="relative flex items-center justify-between">
                                <span
                                    class="pr-3 bg-white text-lg font-bold text-gray-900"> Commission Rates </span>
                                </div>
                            </div>
                            <div>
                                <div class="rounded-md bg-gray-50 p-4 mt-2">
                                    <form @submit.prevent="submit">
                                        <div class="grid grid-cols-4 gap-6">
                                            <div class="col-span-4 sm:col-span-2 lg:col-span-1">
                                                <label
                                                    for="commission_rate"
                                                    class="block text-sm font-medium text-gray-700 required">Commission Rate %</label>
                                                <input
                                                    type="text"
                                                    v-model="form.commission_rate"
                                                    name="commission_rate"
                                                    id="commission_rate"
                                                    autocomplete="name"
                                                    :class="{'border-red-500': form.errors['commission_rate']}"
                                                    class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-gray-900 focus:border-gray-900 sm:text-sm"/>
                                            </div>
                                            <div class="col-span-4 sm:col-span-2 lg:col-span-1">
                                                <label
                                                    for="commission_rate"
                                                    class="block text-sm font-medium text-gray-700 required">Start Date</label>
                                                <input
                                                    type="date"
                                                    v-model="form.start_date"
                                                    name="start_date"
                                                    id="start_date"
                                                    autocomplete="name"
                                                    :class="{'border-red-500': form.errors['start_date']}"
                                                    class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-gray-900 focus:border-gray-900 sm:text-sm"/>
                                            </div>
                                            <div class="col-span-4 sm:col-span-2 lg:col-span-1">
                                                <label
                                                    for="end_date"
                                                    class="block text-sm font-medium text-gray-700 required">End Date</label>
                                                <input
                                                    type="date"
                                                    v-model="form.end_date"
                                                    name="end_date"
                                                    id="end_date"
                                                    autocomplete="name"
                                                    :class="{'border-red-500': form.errors['end_date']}"
                                                    class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-gray-900 focus:border-gray-900 sm:text-sm"/>
                                            </div>
                                            <div class="col-span-4 sm:col-span-2 lg:col-span-1 ml-auto mt-auto">
                                                <button
                                                    type="submit"
                                                    class="ml-5 bg-pitch-purple border border-transparent rounded-md shadow-sm py-2 px-4 inline-flex justify-center text-sm font-medium text-white hover:bg-dark-pitch-purple focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-700"
                                                >Create New Commission Rate</button>
                                            </div>
                                        </div>
                                    </form>
                                </div>
                                <div class="rounded-md bg-gray-50 p-2 mt-5">
                                    <div class="flow-root">
                                        <div class="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
                                            <div class="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
                                            <table class="min-w-full divide-y divide-gray-300">
                                                <thead>
                                                    <tr>
                                                        <th scope="col" class="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-3">Commission Rate %</th>
                                                        <th scope="col" class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Start Date</th>
                                                        <th scope="col" class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">End Date</th>
                                                    </tr>
                                                </thead>
                                                <tbody class="bg-white">
                                                <tr v-for="commission_rate in commissionRates" :key="commission_rate.id" class="even:bg-gray-50">
                                                    <td class="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-3">{{ commission_rate.rate }}</td>
                                                    <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-500">{{ commission_rate.start_date }}</td>
                                                    <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-500">{{ commission_rate.end_date }}</td>
                                                </tr>
                                                </tbody>
                                            </table>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    </div>
</template>
